import React from 'react'
import Link from 'next/link'
import styled from '@emotion/styled'
import { css } from '@emotion/react'
import { colors, mediaQueriesByDevice } from 'parameters/index'
import { ArrowIcon } from 'components/patterns/icons'

export interface AccordianItemDetails {
	title: string
	content: string
	ctaText: string
	url: string
}

interface Props {
	item: AccordianItemDetails
	isActive: boolean
	setActiveItem: () => void
}

export const AccordianItem: React.FC<React.PropsWithChildren<Props>> = ({ item, isActive, setActiveItem }: Props) => (
	<CrossSellItemWrapper isActive={isActive} onClick={setActiveItem}>
		<Title>{item.title}</Title>
		<Content>
			<p>{item.content}</p>
			<div>
				<Link href={item.url} passHref legacyBehavior>
					<CrossSellItemCta>
						<span>{item.ctaText}</span> <ArrowIcon />
					</CrossSellItemCta>
				</Link>
			</div>
		</Content>
	</CrossSellItemWrapper>
)

const Title = styled.div`
	font-size: 24px;
	font-weight: bold;
	text-align: left;
	color: ${colors.secondaryDarkGrey1};
	cursor: pointer;

	${mediaQueriesByDevice.tablet} {
		color: ${colors.black};
	}
`

const Content = styled.div`
	max-height: 0;
	transition: max-height 0.15s ease-out;
	overflow: hidden;
	display: flex;
	flex-direction: column;
`

const CrossSellActiveItem = css`
	border-radius: 8px;
	box-shadow: 0 0 20px 0 rgba(69, 69, 69, 0.16);
	background-color: ${colors.white};

	${Content} {
		margin-top: 20px;
		max-height: 500px;
		transition: max-height 0.75s ease-in;
	}

	${Title} {
		color: ${colors.black};
	}

	${mediaQueriesByDevice.tablet} {
		box-shadow: unset;
	}
`

const CrossSellItemWrapper = styled.div<{ isActive: boolean }>`
	padding: 32px;
	position: relative;

	${({ isActive }) => isActive && CrossSellActiveItem}

	${mediaQueriesByDevice.tablet} {
		padding: 24px;
		background: ${colors.white};
		box-shadow: 0px 0px 16px rgba(0, 0, 0, 0.08);
		border-radius: 9px;
		min-width: 320px;
		height: 100%;
		display: flex;
		flex-direction: column;

		${Content} {
			margin-top: 20px;
			max-height: 500px;
			flex: 1 1 auto;

			p {
				flex: 1 1 auto;
			}
		}
	}
`

const CrossSellItemCta = styled.a`
	font-size: 16px;
	font-weight: 600;
	color: ${colors.primaryPurple1};
	cursor: pointer;
	display: flex;
	align-items: center;
	margin-top: 24px;
	text-decoration: none;

	span {
		margin-right: 5px;
		transition: 0.3s;
	}

	&:hover {
		span {
			margin-right: 10px;
		}
	}
`
